import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { Loader2, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["placed", "processing", "shipped", "delivered", "cancelled"];
const PAY_COLORS = { paid: "text-brand", pending: "text-yellow-400", expired: "text-zinc-500", failed: "text-red-400" };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (order, status) => {
    try {
      const { data } = await api.put(`/admin/orders/${order.id}/status`, { status });
      toast.success(`Order marked ${status}`);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      if (active?.id === order.id) setActive(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const resetSales = async () => {
    try {
      await api.post("/admin/reset-sales");
      toast.success("Sales reset to zero");
      setConfirmReset(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight">Orders ({orders.length})</h2>
        {orders.length > 0 && (
          <button onClick={() => setConfirmReset(true)} data-testid="reset-sales-btn" className="inline-flex items-center gap-2 rounded-full border border-red-500/40 text-red-400 px-4 py-2 text-xs font-bold uppercase tracking-wide hover:bg-red-500/10 active:scale-95 transition">
            <Trash2 className="h-4 w-4" /> Reset Sales
          </button>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : orders.length === 0 ? (
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 py-8">No orders yet</p>
      ) : (
        <div className="border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left p-3">Order</th>
                <th className="text-left p-3 hidden sm:table-cell">Customer</th>
                <th className="text-left p-3">Total</th>
                <th className="text-left p-3">Payment</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {orders.map((o) => (
                <tr key={o.id} data-testid={`admin-order-${o.id}`} className="hover:bg-zinc-900/50 cursor-pointer" onClick={() => setActive(o)}>
                  <td className="p-3 font-mono">#{o.order_no}</td>
                  <td className="p-3 hidden sm:table-cell text-zinc-400 truncate max-w-[160px]">{o.user_email}</td>
                  <td className="p-3 font-mono">{inr(o.total)}</td>
                  <td className={`p-3 font-mono text-xs uppercase ${PAY_COLORS[o.payment_status] || ""}`}>{o.payment_status}</td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={o.status}
                      onChange={(e) => updateStatus(o, e.target.value)}
                      data-testid={`order-status-select-${o.id}`}
                      className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs font-mono uppercase tracking-widest"
                    >
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-zinc-900">{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Order #{active.order_no}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3 font-mono text-xs">
                  <div><p className="text-zinc-500 uppercase tracking-widest">Payment</p><p className={PAY_COLORS[active.payment_status]}>{active.payment_status}</p></div>
                  <div><p className="text-zinc-500 uppercase tracking-widest">Status</p><p>{active.status}</p></div>
                  {active.utr && <div><p className="text-zinc-500 uppercase tracking-widest">UTR</p><p>{active.utr}</p></div>}
                  {active.transaction_id && <div><p className="text-zinc-500 uppercase tracking-widest">Txn ID</p><p className="truncate">{active.transaction_id}</p></div>}
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Items</p>
                  <div className="space-y-2">
                    {active.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <img src={it.image} alt={it.name} className="h-12 w-10 object-cover bg-zinc-900" />
                        <div className="flex-1"><p className="text-sm">{it.name}</p><p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{it.size} × {it.qty}</p></div>
                        <span className="font-mono text-xs">{inr(it.price * it.qty)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Shipping Address</p>
                  <p className="text-zinc-300 text-sm">
                    {active.shipping_address?.full_name} · {active.shipping_address?.phone}<br />
                    {active.shipping_address?.line1}{active.shipping_address?.line2 ? `, ${active.shipping_address.line2}` : ""}<br />
                    {active.shipping_address?.city}, {active.shipping_address?.state} - {active.shipping_address?.pincode}
                  </p>
                </div>
                <div className="border-t border-zinc-800 pt-3 flex justify-between font-mono">
                  <span className="uppercase tracking-widest text-xs text-zinc-500">Total</span><span>{inr(active.total)}</span>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                      <button key={s} onClick={() => updateStatus(active, s)} className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition ${active.status === s ? "bg-brand text-black border-brand" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>{s}</button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all sales?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Every order will be permanently deleted and revenue, order counts and stats will reset to 0. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={resetSales} data-testid="confirm-reset-sales-btn" className="bg-red-500 hover:bg-red-600">Reset to Zero</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
